/**
 * The only way the ported game talks to the outside world (docs/architecture.md section 6).
 *
 * `GameBackend` is the subset of the transport-agnostic `GameServices` facade (src/client) that the game
 * code uses. Hosts (src/main.ts, src/headless.ts, tests) create a `GameServices` instance for the chosen
 * transport (in-process/offline, HTTP, WebSocket) and inject it into `new FWP({ library, services })`.
 * Gameplay code never sees transports, protocol envelopes or VK.
 */
import type { GameServices } from '../client/game-services';

export type GameBackend = Pick<
  GameServices,
  | 'ensureSession'
  // The game's accounts (nickname + password): the start restores the stored session, else the sign-in screen signs
  // up or signs in; guestSignIn = a guest is allowed instead (headless hosts, tests, ?autoguest=1 offline).
  | 'guestSignIn'
  | 'restoreFailure'
  | 'restoreSession'
  | 'signUp'
  | 'signIn'
  // E-mail sign-in where the server offers it (the sign-in screen, the account dialog's e-mail dialog).
  | 'emailAvailable'
  | 'signUpWithEmail'
  | 'signInWithEmail'
  | 'requestPasswordReset'
  | 'emailStatus'
  | 'changeEmail'
  | 'resendEmail'
  | 'signOut'
  // Self-service deletion of the account (settings screen; the password confirms it, a guest's session alone).
  | 'deleteAccount'
  | 'getMyProfile'
  // 'in-process': the backend runs inside the page (offline / memory); the sign-in screen says where progress is kept.
  | 'transportKind'
  | 'loadProfile'
  | 'saveProfile'
  // Server-side meta-game operations (player.command): the game's way to change the canonical state at trust level L2.
  | 'command'
  | 'getServerTime'
  | 'getLeaderboard'
  // The clans of the players (src/game/runtime/clan-hub.ts: the clans tab, the card of a clan, the best clans; the
  // clan emblems of the tops and of the assault map come with their entries). Test doubles may leave them out: the
  // clans tab then says that clans are not available.
  | 'getMyClan'
  | 'getClan'
  | 'getClanMembers'
  | 'listClansPage'
  | 'createClan'
  | 'updateClan'
  | 'joinClan'
  | 'requestClan'
  | 'cancelClanRequest'
  | 'listClanRequests'
  | 'decideClanRequest'
  | 'kickClanMember'
  | 'setClanRole'
  | 'transferClan'
  | 'leaveClan'
  | 'disbandClan'
  | 'getClanHistoryPage'
  // The chat (src/game/runtime/chat.ts: the main map's chat button and panel). Test doubles may leave it out: the game
  // then shows no chat button.
  | 'chatAvailable'
  | 'chatLimits'
  | 'chatChannels'
  | 'chatHistory'
  | 'chatSend'
  | 'chatSubscribe'
  | 'chatUnsubscribe'
  | 'chatMarkRead'
  | 'getAssaultZones'
  | 'reportAssault'
  | 'saveReplay'
  | 'listReplays'
  | 'getReplay'
  | 'getPublicProfile'
  // The annihilator auction screen (design D7); buying is the command 'auction.buy'. Test doubles may leave it out.
  | 'getAuction'
  // Server events (profile.changed, ...) and the connection status, consumed by the game's non-blocking event queue
  // (src/game/runtime/net-events.ts). Test doubles may leave them out; the game then just gets no events.
  | 'on'
  | 'connectionStatus'
  | 'onConnectionStatus'
  // Missed events that cannot be replayed after a reconnect: the game reloads the profile (net-events.ts).
  | 'onEventGap'
  // One active session per account: a sign-in elsewhere ended this one (FWP.net_session_replaced). Optional in doubles.
  | 'sessionReplaced'
  | 'onSessionReplaced'
  // Online "just" battles (design D5): the lobby rooms and the battle sessions played in lockstep
  // (src/game/runtime/online-lobby.ts, online-match.ts). Test doubles may leave them out: the online tab then says
  // that online play is not available.
  | 'listRoomsPage'
  | 'getRoom'
  | 'createRoom'
  | 'joinRoom'
  | 'leaveRoom'
  | 'setReady'
  | 'updateRoomSettings'
  | 'startRoom'
  | 'getSession'
  | 'createLockstep'
  | 'resumeLockstep'
  | 'finishBattle'
  | 'forfeitBattle'
  | 'disputeBattle'
>;

/** Save format version of the JSON game state stored in the backend profile (`PlayerProfile.gameState`). */
export const GAME_STATE_FORMAT = 'fwp-state-v1';

/** Stable positive 30-bit numeric id for a backend player id (the game logic keys players by numbers). */
export function numericPlayerId(playerId: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < playerId.length; i++) {
    h ^= playerId.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return (h & 0x3fffffff) + 1;
}
